"use client";

/**
 * Web outbox for share / delete operations.
 *
 * Mirrors iOS `PendingCommits` (see `FaceOff/Shared/Models/LocalCache.swift`
 * and `SessionsViewModel.swift` drain logic). Ensures user actions
 * survive transient network failures, page reloads, and tab close.
 *
 * Operations persisted to localStorage under a per-user key. Drain
 * fires on:
 *   - `online` event (NWPathMonitor reconnect equivalent)
 *   - `visibilitychange → visible` (scenePhase .active equivalent)
 *   - Module load (catches ops queued in a prior session)
 *   - Opportunistically after the wrapped API call returns
 *
 * Server endpoints for these operations are idempotent — re-firing
 * a successful op is a no-op on the server side (DELETE on missing
 * session = 404 swallowed; share with existing email = no-op).
 *
 * NOT-CURRENTLY-WRAPPED operations (deliberate scope): record edits,
 * folder ops, memory mutations. These either have their own retry
 * paths (record save already retries via the streaming reconciler)
 * or fail loud enough that silent queuing would be misleading.
 */

import { deleteSession, shareSession, unshareSession, leaveSharedSession } from "./api";
import { getAuthState } from "./auth";
import type { ShareSessionRequest } from "./types";

// =============================================================================
// Op definitions
// =============================================================================

type OpBase = {
  id: string;
  createdAt: number;
  retries: number;
  lastError?: string;
};

export type OutboxOp =
  | (OpBase & { kind: "deleteSession"; sessionId: string })
  | (OpBase & { kind: "shareSession"; sessionId: string; payload: ShareSessionRequest })
  | (OpBase & { kind: "unshareSession"; sessionId: string; email: string })
  | (OpBase & { kind: "leaveSharedSession"; sessionId: string });

const MAX_RETRIES = 5;
const STORAGE_PREFIX = "trio:outbox:";

// =============================================================================
// Storage
// =============================================================================

function storageKey(): string | null {
  const auth = getAuthState();
  if (!auth?.email) return null;
  // Lowercase + base64 the email to keep the key URL-safe-ish + avoid
  // collisions on stray whitespace differences across login flows.
  const normalized = auth.email.trim().toLowerCase();
  return `${STORAGE_PREFIX}${normalized}`;
}

function load(): OutboxOp[] {
  if (typeof window === "undefined") return [];
  const key = storageKey();
  if (!key) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive type narrowing — corrupted localStorage shouldn't crash drain.
    return parsed.filter((p): p is OutboxOp =>
      typeof p === "object" &&
      p !== null &&
      typeof (p as { kind?: unknown }).kind === "string"
    );
  } catch {
    return [];
  }
}

function save(ops: OutboxOp[]): void {
  if (typeof window === "undefined") return;
  const key = storageKey();
  if (!key) return;
  try {
    if (ops.length === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(ops));
    }
  } catch {
    // localStorage might be full or unavailable (private mode). Silent
    // drop is acceptable here — the operation will be lost, but the
    // immediate API try already happened in the wrapper, so this only
    // affects the failure-retry path which the user will notice as a
    // missed action and can re-attempt.
  }
}

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// =============================================================================
// Drain
// =============================================================================

let drainInFlight = false;
let drainQueued = false;

async function executeOp(op: OutboxOp): Promise<void> {
  switch (op.kind) {
    case "deleteSession":
      await deleteSession(op.sessionId);
      return;
    case "shareSession":
      await shareSession(op.sessionId, op.payload);
      return;
    case "unshareSession":
      await unshareSession(op.sessionId, op.email);
      return;
    case "leaveSharedSession":
      await leaveSharedSession(op.sessionId);
      return;
  }
}

/**
 * Try executing each pending op once. Successes are removed; failures
 * bump retry count. Concurrent calls coalesce — the second caller
 * sets a flag and the in-flight drain runs an extra pass before
 * returning to flush items enqueued during execution.
 */
export async function drain(): Promise<void> {
  if (drainInFlight) {
    drainQueued = true;
    return;
  }
  drainInFlight = true;
  try {
    do {
      drainQueued = false;
      const ops = load();
      if (ops.length === 0) break;
      const survivors: OutboxOp[] = [];
      for (const op of ops) {
        try {
          await executeOp(op);
        } catch (err: unknown) {
          const retried: OutboxOp = {
            ...op,
            retries: op.retries + 1,
            lastError: err instanceof Error ? err.message : String(err),
          };
          if (retried.retries < MAX_RETRIES) {
            survivors.push(retried);
          } else {
            // Drop after MAX_RETRIES. Log to console for diagnostics —
            // the user already saw the optimistic UI update so they
            // think the operation succeeded. A backend retry sweeper
            // could pick these up but for now we surface only on the
            // console.
            console.warn(
              `[outbox] dropping ${op.kind} after ${MAX_RETRIES} retries`,
              op
            );
          }
        }
      }
      save(survivors);
    } while (drainQueued);
  } finally {
    drainInFlight = false;
  }
}

// Discriminated input type — keeps TS variant-aware so each call
// site only provides the fields its op kind requires.
type OutboxOpInput =
  | { kind: "deleteSession"; sessionId: string }
  | { kind: "shareSession"; sessionId: string; payload: ShareSessionRequest }
  | { kind: "unshareSession"; sessionId: string; email: string }
  | { kind: "leaveSharedSession"; sessionId: string };

function enqueue(op: OutboxOpInput): void {
  const ops = load();
  ops.push({
    ...op,
    id: generateId(),
    createdAt: Date.now(),
    retries: 0,
  } as OutboxOp);
  save(ops);
}

// =============================================================================
// Public API — fire-and-forget wrappers
// =============================================================================
//
// Each wrapper tries the API call once. On failure (network, 5xx, etc.),
// the op is enqueued and drain() is scheduled. Callers should treat
// these as never-throwing — UI updates optimistically and the outbox
// guarantees eventual consistency.

export async function outboxDeleteSession(sessionId: string): Promise<void> {
  try {
    await deleteSession(sessionId);
  } catch (err) {
    console.warn("[outbox] deleteSession failed, queuing for retry", err);
    enqueue({ kind: "deleteSession", sessionId });
    // Background re-drain in case the failure was transient.
    setTimeout(() => void drain(), 2000);
  }
}

export async function outboxShareSession(
  sessionId: string,
  payload: ShareSessionRequest
): Promise<void> {
  try {
    await shareSession(sessionId, payload);
  } catch (err) {
    console.warn("[outbox] shareSession failed, queuing for retry", err);
    enqueue({ kind: "shareSession", sessionId, payload });
    setTimeout(() => void drain(), 2000);
  }
}

export async function outboxUnshareSession(
  sessionId: string,
  email: string
): Promise<void> {
  try {
    await unshareSession(sessionId, email);
  } catch (err) {
    console.warn("[outbox] unshareSession failed, queuing for retry", err);
    enqueue({ kind: "unshareSession", sessionId, email });
    setTimeout(() => void drain(), 2000);
  }
}

export async function outboxLeaveSharedSession(sessionId: string): Promise<void> {
  try {
    await leaveSharedSession(sessionId);
  } catch (err) {
    console.warn("[outbox] leaveSharedSession failed, queuing for retry", err);
    enqueue({ kind: "leaveSharedSession", sessionId });
    setTimeout(() => void drain(), 2000);
  }
}

// =============================================================================
// Lifecycle
// =============================================================================

let lifecycleInstalled = false;

/**
 * Wire browser event listeners that re-drain on connectivity recovery
 * or app foreground. Call once from a top-level component effect.
 * Idempotent.
 */
export function installOutboxLifecycle(): void {
  if (lifecycleInstalled) return;
  if (typeof window === "undefined") return;
  lifecycleInstalled = true;

  // Drain on network recovery.
  window.addEventListener("online", () => {
    void drain();
  });

  // Drain on app foreground (tab becomes visible after being hidden).
  // Matches iOS scenePhase .active opportunistic drain.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void drain();
  });

  // Drain on module load — pick up anything left from a prior session.
  void drain();
}

// =============================================================================
// Diagnostics
// =============================================================================

/** Number of currently-pending operations. */
export function pendingCount(): number {
  return load().length;
}

/** Drop all pending operations. Intended for debug / sign-out cleanup. */
export function clearAll(): void {
  save([]);
}
